import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { toast } from "react-toastify";

import BannerAccordion from "./bannerAccordian";
import ProductvariantBanner from "./productvariantAccordian";
import {
  postDynamicPage,
  getDynamicPageById,
  deleteDynamic_Page,
  updateDynamic_page,
} from "../auth/service";
import WarrantyAccordion from "./warrantyAccordian";

const CategoryDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams(); // id can be "new" or existing
  const isNew = id === "new";

  const pageName = location.state?.pageName || "Home";

  const [loading, setLoading] = useState(false);

  const [bannerData, setBannerData] = useState({
    webImages: [],
    mobileImages: [],
    webVideos: [],
    mobileVideos: [],
    seoContent: "",
    pageName: "",
  });

  const [productVariants, setProductVariants] = useState([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [warranties, setWarranties] = useState([]);

  /* ---------------- FETCH PAGE BY ID ---------------- */
  useEffect(() => {
    if (!id || isNew) return; // skip fetching for new page

    const fetchPage = async () => {
      try {
        setLoading(true);
        const res = await getDynamicPageById(id);
        const page = res?.data;

        console.log("this is perticular page data", page);

        setBannerData({
          webImages: page.web_image_url || [], // <-- API field
          mobileImages: page.mobile_image_url || [], // <-- API field
          webVideos: page.web_video_url || [], // <-- API field
          mobileVideos: page.mobile_video_url || [], // <-- API field
          seoContent: page.text || "",
          pageName: page.page_name ?? "",
        });

        // Variants
        setProductVariants(
          (page.variant || []).map((v) => ({
            id: crypto.randomUUID(),
            variant: v.variant,
            productName: v.name,
            title: v.title,
            description: v.description,
            price: {
              exShowroom: v.price,
              onRoad: v.offer_price,
            },
            specification: {
              batteryCapacity: v.specification?.battery_capacity,
              speed: v.specification?.speed,
              range: v.specification?.range,
              chargingTime: v.specification?.charging,
              display: v.specification?.display,
              reverseMode: v.specification?.reverse_mode,
              colours: v.specification?.colours,
              warranty: v.specification?.warranty,
            },
            // warranty: page.warranty || [],
            // warranty: page.warranty ? page.warranty.map((w) => ({ ...w })) : [],
          }))
        );
        setWarranties(
          page.warranty
            ? page.warranty.map((w) => ({
                id: crypto.randomUUID(),
                ...w,
              }))
            : []
        );
      } catch (err) {
        toast.error("Failed to load page details");
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [id, isNew]);

  /* ---------------- SAVE / UPDATE ---------------- */

  const handleSave = async () => {
    if (!productVariants.length) {
      toast.error("Please add at least one product variant");
      return;
    }

    const plainText = bannerData.seoContent?.replace(/<[^>]*>/g, "").trim();

    if (!plainText) {
      toast.error("Banner text is required");
      return;
    }

    const payload = {
      page_name: bannerData.pageName || pageName,
      web_image_url: bannerData.webImages || [],
      mobile_image_url: bannerData.mobileImages || [],
      web_video_url: bannerData.webVideos || [],
      mobile_video_url: bannerData.mobileVideos || [],
      // text: bannerData.seoContent || "",
      ...(bannerData.seoContent && { text: bannerData.seoContent }),

      variant: productVariants.map((p) => {
        // Build specification object, only include non-empty keys
        const spec = {};
        if (p.specification?.batteryCapacity)
          spec.battery_capacity = p.specification.batteryCapacity;
        if (p.specification?.speed) spec.speed = p.specification.speed;
        if (p.specification?.range) spec.range = p.specification.range;
        if (p.specification?.chargingTime)
          spec.charging = p.specification.chargingTime;
        if (p.specification?.display) spec.display = p.specification.display;
        if (p.specification?.reverseMode)
          spec.reverse_mode = p.specification.reverseMode;
        if (p.specification?.colours) spec.colours = p.specification.colours;
        if (p.specification?.warranty) spec.warranty = p.specification.warranty;

        // Build variant object
        const variantObj = {
          variant: p.variant,
          name: p.productName,
        };

        if (Object.keys(spec).length) variantObj.specification = spec;
        if (p.price?.exShowroom) variantObj.price = String(p.price.exShowroom);
        if (p.price?.onRoad) variantObj.offer_price = String(p.price.onRoad);
        if (p.title) variantObj.title = p.title;
        if (p.description) variantObj.description = p.description;

        return variantObj;
      }),
    };

    const cleanWarranty = warranties
      .filter((w) => w.plan || w.text || w.price)
      .map(({ plan, price, text }) => ({ plan, price, text }));

    if (cleanWarranty.length) payload.warranty = cleanWarranty;

    try {
      if (isNew) {
        await postDynamicPage(payload);
        toast.success("Page created successfully");
      } else {
        await updateDynamic_page(id, payload);
        toast.success("Page updated successfully");
      }

      navigate("/dashboard/PageCategoryDetails/page-category-details");
    } catch (err) {
      console.error("API Error:", err);

      const apiData = err?.response?.data || err;

      if (apiData?.success === false) {
        const messages = [];

        // Main API message
        if (apiData.message) {
          messages.push(apiData.message);
        }

        // Validation errors
        if (Array.isArray(apiData.errors)) {
          apiData.errors.forEach((msg) => {
            messages.push(msg.replace(/"/g, ""));
          });
        }

        // 🔥 ONE toast only
        toast.error(messages.join("\n"));
        return;
      }

      // Fallback
      toast.error("Failed to save page");
    }
  };

  const handleDeleteClick = () => {
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteDynamic_Page(id);
      toast.success("Page deleted successfully");
      setOpenDeleteDialog(false);
      navigate("/dashboard/PageCategoryDetails/page-category-details");
    } catch (err) {
      toast.error("Failed to delete page");
      console.error(err);
    }
  };

  /* ---------------- UI ---------------- */
  return (
    <div className="store-container">
      <div className="header-container">
        <span className="header">
          <ArrowBackIosIcon onClick={() => navigate(-1)} />
          {pageName} Page Details
        </span>
        <div className="btn-container" style={{ display: "flex", gap: "10px" }}>
          <Button variant="contained" onClick={handleSave} disabled={loading}>
            {isNew ? "Save" : "Update"}
          </Button>
          {!isNew && (
            <Button
              variant="outlined"
              color="error"
              onClick={handleDeleteClick}
            >
              Delete
            </Button>
          )}
        </div>
      </div>

      <BannerAccordion
        title="Fold 1 - Banner"
        // data={{ ...bannerData, pageName: bannerData.pageName || pageName }}
        data={bannerData}
        setData={setBannerData}
        pageName={pageName}
      />

      <ProductvariantBanner
        title="Fold 2 - Product Variants"
        products={productVariants}
        setProducts={setProductVariants}
      />
      <WarrantyAccordion
        title="Fold 3 - Warranty"
        warranties={warranties}
        setWarranties={setWarranties}
      />

      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">Delete Page</DialogTitle>

        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the page{" "}
            <strong>{bannerData.pageName || "this page"}</strong>?
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CategoryDetails;
